import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { Plus, Check, Trash2, FileText, Edit, QrCode, PenTool } from 'lucide-react';
import { toast } from 'sonner';

export const CertificateTemplates = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Form state
  const [name, setName] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [studentNameX, setStudentNameX] = useState(400);
  const [studentNameY, setStudentNameY] = useState(350);
  const [studentNameSize, setStudentNameSize] = useState(48);
  const [courseNameX, setCourseNameX] = useState(400);
  const [courseNameY, setCourseNameY] = useState(450);
  const [courseNameSize, setCourseNameSize] = useState(36);
  const [dateX, setDateX] = useState(400);
  const [dateY, setDateY] = useState(550);
  const [dateSize, setDateSize] = useState(24);
  const [codeX, setCodeX] = useState(400);
  const [codeY, setCodeY] = useState(650);
  const [codeSize, setCodeSize] = useState(20);

  useEffect(() => {
    if (user && !['superadmin', 'admin'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    fetchTemplates();
  }, [user, navigate]);

  const fetchTemplates = async () => {
    try {
      const response = await api.get('/certificate-templates/');
      setTemplates(response.data);
    } catch (error) {
      toast.error('Error al cargar plantillas');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setName('');
    setImageUrl('');
    setStudentNameX(400);
    setStudentNameY(350);
    setStudentNameSize(48);
    setCourseNameX(400);
    setCourseNameY(450);
    setCourseNameSize(36);
    setDateX(400);
    setDateY(550);
    setDateSize(24);
    setCodeX(400);
    setCodeY(650);
    setCodeSize(20);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const templateData = {
      name,
      image_url: imageUrl,
      student_name_x: parseInt(studentNameX),
      student_name_y: parseInt(studentNameY),
      student_name_size: parseInt(studentNameSize),
      course_name_x: parseInt(courseNameX),
      course_name_y: parseInt(courseNameY),
      course_name_size: parseInt(courseNameSize),
      date_x: parseInt(dateX),
      date_y: parseInt(dateY),
      date_size: parseInt(dateSize),
      code_x: parseInt(codeX),
      code_y: parseInt(codeY),
      code_size: parseInt(codeSize)
    };

    try {
      await api.post('/certificate-templates/', templateData);
      toast.success('Plantilla creada');
      setDialogOpen(false);
      resetForm();
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear plantilla');
    }
  };

  const handleActivate = async (templateId) => {
    try {
      await api.put(`/certificate-templates/${templateId}/activate/`);
      toast.success('Plantilla activada');
      fetchTemplates();
    } catch (error) {
      toast.error('Error al activar plantilla');
    }
  };

  const handleDelete = async (templateId) => {
    if (!window.confirm('¿Estás seguro de eliminar esta plantilla?')) return;
    
    try {
      await api.delete(`/certificate-templates/${templateId}/`);
      toast.success('Plantilla eliminada');
      fetchTemplates();
    } catch (error) {
      toast.error('Error al eliminar plantilla');
    }
  };

  if (loading) return <div className="p-8">Cargando...</div>;

  return (
    <div className="p-6 md:p-12 bg-secondary min-h-screen" data-testid="certificate-templates-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary mb-2">
              Plantillas de Certificados
            </h1>
            <p className="text-muted-foreground">Gestiona plantillas para generar certificados automáticamente</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => navigate('/admin/certificate-templates/new')} data-testid="create-template-visual-btn">
              <Plus className="w-4 h-4 mr-2" />
              Nueva Plantilla (Editor Visual)
            </Button>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" className="hidden" data-testid="create-template-btn">
                <Plus className="w-4 h-4 mr-2" />
                Nueva Plantilla
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Crear Nueva Plantilla de Certificado</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Nombre de la Plantilla *</Label>
                  <Input
                    id="name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder="Ej: Plantilla Principal"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="imageUrl">URL de Imagen de Fondo *</Label>
                  <Input
                    id="imageUrl"
                    value={imageUrl}
                    onChange={(e) => setImageUrl(e.target.value)}
                    placeholder="Pega la URL de la imagen desde Biblioteca de Medios"
                    required
                  />
                  <p className="text-xs text-muted-foreground">
                    Tip: Sube una imagen en Biblioteca de Medios y copia su URL aquí
                  </p>
                </div>

                <div className="border-t pt-4">
                  <h3 className="font-semibold mb-4">Posiciones del Texto (coordenadas X, Y)</h3>
                  
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="space-y-2">
                      <Label>Nombre Estudiante - X</Label>
                      <Input type="number" value={studentNameX} onChange={(e) => setStudentNameX(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Y</Label>
                      <Input type="number" value={studentNameY} onChange={(e) => setStudentNameY(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Tamaño Fuente</Label>
                      <Input type="number" value={studentNameSize} onChange={(e) => setStudentNameSize(e.target.value)} />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="space-y-2">
                      <Label>Nombre Curso - X</Label>
                      <Input type="number" value={courseNameX} onChange={(e) => setCourseNameX(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Y</Label>
                      <Input type="number" value={courseNameY} onChange={(e) => setCourseNameY(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Tamaño Fuente</Label>
                      <Input type="number" value={courseNameSize} onChange={(e) => setCourseNameSize(e.target.value)} />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4 mb-4">
                    <div className="space-y-2">
                      <Label>Fecha - X</Label>
                      <Input type="number" value={dateX} onChange={(e) => setDateX(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Y</Label>
                      <Input type="number" value={dateY} onChange={(e) => setDateY(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Tamaño Fuente</Label>
                      <Input type="number" value={dateSize} onChange={(e) => setDateSize(e.target.value)} />
                    </div>
                  </div>

                  <div className="grid grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label>Código - X</Label>
                      <Input type="number" value={codeX} onChange={(e) => setCodeX(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Y</Label>
                      <Input type="number" value={codeY} onChange={(e) => setCodeY(e.target.value)} />
                    </div>
                    <div className="space-y-2">
                      <Label>Tamaño Fuente</Label>
                      <Input type="number" value={codeSize} onChange={(e) => setCodeSize(e.target.value)} />
                    </div>
                  </div>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button type="submit" data-testid="save-template-btn">
                    Crear Plantilla
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Info Card */}
        <Card className="mb-8 bg-blue-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex gap-3">
              <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-900 mb-2">Cómo usar las plantillas:</h3>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li>Diseña tu certificado en tu editor favorito (Photoshop, Canva, etc.)</li>
                  <li>Exporta como imagen (JPG o PNG) de 1920x1080px o similar</li>
                  <li>Sube la imagen a Biblioteca de Medios</li>
                  <li>Copia la URL y créala como plantilla aquí</li>
                  <li>Ajusta las posiciones X,Y donde aparecerá el texto</li>
                  <li>Activa la plantilla - los certificados se generarán automáticamente</li>
                </ol>
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {templates.length === 0 ? (
            <Card className="col-span-full">
              <CardContent className="py-12 text-center">
                <p className="text-muted-foreground">No hay plantillas creadas aún</p>
              </CardContent>
            </Card>
          ) : (
            templates.map((template) => (
              <Card key={template.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <h3 className="text-lg font-semibold mb-1">{template.name}</h3>
                      {template.is_active && (
                        <Badge className="bg-green-600">
                          <Check className="w-3 h-3 mr-1" />
                          Activa
                        </Badge>
                      )}
                    </div>
                    <div className="flex gap-2">
                      {!template.is_active && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleActivate(template.id)}
                          data-testid={`activate-template-${template.id}`}
                        >
                          Activar
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => navigate(`/admin/certificate-templates/${template.id}`)}
                        data-testid={`edit-template-${template.id}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(template.id)}
                        data-testid={`delete-template-${template.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {template.image_url && (
                    <div className="w-full h-48 bg-muted rounded-lg overflow-hidden mb-4">
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}${template.image_url}`}
                        alt={template.name}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}
                  
                  <div className="text-xs text-muted-foreground space-y-1">
                    <p>Nombre: X:{template.student_name_x}, Y:{template.student_name_y}</p>
                    <p>Curso: X:{template.course_name_x}, Y:{template.course_name_y}</p>
                    <p>Fecha: X:{template.date_x}, Y:{template.date_y}</p>
                    <p>Código: X:{template.code_x}, Y:{template.code_y}</p>
                    {template.signature_url && (
                      <p className="flex items-center gap-1">
                        <PenTool className="w-3 h-3" />
                        Firma: X:{template.signature_x}, Y:{template.signature_y}
                      </p>
                    )}
                    {template.qr_x && template.qr_y && (
                      <p className="flex items-center gap-1">
                        <QrCode className="w-3 h-3" />
                        QR: X:{template.qr_x}, Y:{template.qr_y}
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default CertificateTemplates;
